'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { useEffect, useState } from 'react'

interface PasswordProtectionProps {
  children: React.ReactNode
  password?: string
}

export function PasswordProtection({
  children,
  password = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123',
}: PasswordProtectionProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [inputPassword, setInputPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const authStatus = localStorage.getItem('admin-authenticated')
    if (authStatus === 'true') {
      setIsAuthenticated(true)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500))

    if (inputPassword === password) {
      setIsAuthenticated(true)
      localStorage.setItem('admin-authenticated', 'true')
      // Optional: Set expiration (24 hours)
      localStorage.setItem(
        'admin-auth-expires',
        (Date.now() + 24 * 60 * 60 * 1000).toString(),
      )
    }
    else {
      setError('Incorrect password. Please try again.')
    }

    setIsLoading(false)
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
    setInputPassword('')
    localStorage.removeItem('admin-authenticated')
    localStorage.removeItem('admin-auth-expires')
  }

  // Check if authentication has expired
  useEffect(() => {
    const checkExpiration = () => {
      const expires = localStorage.getItem('admin-auth-expires')
      if (expires && Date.now() > parseInt(expires)) {
        handleLogout()
      }
    }

    checkExpiration()
    // Check every minute
    const interval = setInterval(checkExpiration, 60000)
    return () => clearInterval(interval)
  }, [])

  if (isAuthenticated) {
    return (
      <div className="relative">
        {/* Logout button */}
        <div className="fixed right-4 top-4 z-50">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="bg-background/80 backdrop-blur-sm"
          >
            <Lock className="mr-2 size-4" />
            Logout
          </Button>
        </div>
        {children}
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="size-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Admin Access</CardTitle>
          <p className="text-muted-foreground">
            Please enter the password to access the admin panel
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={inputPassword}
                  onChange={e => setInputPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <div className="text-center text-sm text-red-500">{error}</div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !inputPassword.trim()}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="mr-2 size-4" />
                  Access Admin Panel
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
